import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Clear as ClearIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { ChangeEvent } from "react";

import {
  MergeFileReport,
  MergeReportKind,
} from "features/adminUsers/adminUsers";

/** One slot of the Merge reports dialog: the chosen file, its upload, and the
 *  backend's check of it after a Merge attempt. */
export type MergeSlotState = {
  file: File | null;
  stagingKey: string | null;
  step: "empty" | "uploading" | "ready" | "error";
  uploadError: string | null;
  report: MergeFileReport | null;
  showExample: boolean;
};

export const EMPTY_MERGE_SLOT: MergeSlotState = {
  file: null,
  stagingKey: null,
  step: "empty",
  uploadError: null,
  report: null,
  showExample: false,
};

const ACCEPT =
  ".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// The shape the backend accepts (merge_validation.py), shown as a small table.
// Names are made up: this is a format sample, not anybody's data.
const SLOT_COPY: Record<
  MergeReportKind,
  { title: string; caption: string; header: string[]; rows: string[][] }
> = {
  no_matches: {
    title: "No matches file",
    caption:
      "2 columns: s3_name, and the employee code you added (or No Match, or empty). Header cell B1 may stay empty.",
    header: ["s3_name", ""],
    rows: [
      ["JANE DOE - 0471", "A41W"],
      ["JOHN ROE - 9429", "No Match"],
    ],
  },
  several_matches: {
    title: "Several matches file",
    caption:
      "3 columns: s3_name, site_name, employee_id - each folder once. Delete the wrong candidates or set employee_id to No Match.",
    header: ["s3_name", "site_name", "employee_id"],
    rows: [
      ["Doe Jane - 0421", "Doe, Jane A", "A8OX"],
      ["Roe John - 2323", "Roe, John", "No Match"],
    ],
  },
};

export function mergeSlotTitle(kind: MergeReportKind): string {
  return SLOT_COPY[kind].title;
}

export function MergeReportsFormatExample({ kind }: { kind: MergeReportKind }) {
  const copy = SLOT_COPY[kind];
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{ "& td, & th": { py: 0.25, px: 1, whiteSpace: "nowrap" } }}
      >
        <TableHead>
          <TableRow>
            {copy.header.map((cell, index) => (
              <TableCell key={index}>
                {cell ? (
                  <Typography variant="caption" fontWeight={700}>
                    {cell}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    fontStyle="italic"
                  >
                    (empty or any name)
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {copy.rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>
                  <Typography variant="caption">{cell}</Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

interface MergeReportsSlotProps {
  kind: MergeReportKind;
  slot: MergeSlotState;
  disabled: boolean;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onToggleExample: () => void;
}

export default function MergeReportsSlot({
  kind,
  slot,
  disabled,
  onSelect,
  onClear,
  onToggleExample,
}: MergeReportsSlotProps) {
  const copy = SLOT_COPY[kind];
  const report = slot.report;
  const uploading = slot.step === "uploading";
  const blocked = report?.status === "blocked";
  const rowWord = (count: number) => `${count} row${count === 1 ? "" : "s"}`;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
      <Stack spacing={1}>
        <Box>
          <Typography variant="subtitle2">{copy.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {copy.caption}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon fontSize="small" />}
            disabled={disabled || uploading}
          >
            {slot.file ? "Change file" : "Choose file"}
            <input type="file" hidden accept={ACCEPT} onChange={onSelect} />
          </Button>
          {slot.file && (
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {slot.file.name}
            </Typography>
          )}
          {uploading && (
            <Stack direction="row" gap={0.75} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Uploading…
              </Typography>
            </Stack>
          )}
          {slot.step === "ready" && !report && (
            <Typography variant="caption" color="success.main">
              Ready
            </Typography>
          )}
          {slot.file && !uploading && (
            <IconButton
              size="small"
              aria-label="Remove file"
              onClick={onClear}
              disabled={disabled}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {slot.uploadError && <Alert severity="error">{slot.uploadError}</Alert>}
        {report && blocked && (
          <Alert severity="error">
            <Stack spacing={1}>
              {report.problems.map((problem) => (
                <Box key={problem.code}>
                  <Typography variant="body2" fontWeight={600}>
                    {problem.message}
                  </Typography>
                  <Typography variant="body2">{problem.hint}</Typography>
                </Box>
              ))}
            </Stack>
          </Alert>
        )}
        {report && report.status === "ok" && (
          <Alert severity="info">
            Passed the check - {rowWord(report.rows_to_add)} would be added.
            {report.notes.map((note) => ` ${note}`).join("")}
          </Alert>
        )}
        <Box>
          <Button
            size="small"
            variant="text"
            onClick={onToggleExample}
            disabled={blocked}
          >
            {slot.showExample || blocked
              ? "Expected format"
              : "Show expected format"}
          </Button>
          <Collapse in={slot.showExample || blocked}>
            <MergeReportsFormatExample kind={kind} />
          </Collapse>
        </Box>
      </Stack>
    </Paper>
  );
}
